import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Card, PriceTag, StateScreen } from '@cowoo/shared/ui-web';
import { STALE_TIME, qk } from '@cowoo/shared';
import { api } from '../../lib/api';

/**
 * `/start` — the funnel entry (09-web-member § 3).
 *
 * Prices come from `/public/catalog`, never from a constant: they are data
 * (D-051) and the owner changes them in the backoffice.
 */
export function Start() {
  const { t } = useTranslation();

  const catalog = useQuery({
    queryKey: qk.public.catalog(),
    staleTime: STALE_TIME.publicCatalog,
    queryFn: async () => {
      const { data, error } = await api.GET('/public/catalog');
      if (error || !data) throw error ?? new Error('catalog unavailable');
      return data;
    },
  });

  if (catalog.isError) {
    return <StateScreen title={t('errors.unknown')} />;
  }

  const priceOf = (key: string) => catalog.data?.items.find((item) => item.productKey === key);

  return (
    <main className="max-w-content mx-auto px-5 py-20">
      <h1 className="font-heading text-ink mb-2 text-4xl">{t('member.start.title')}</h1>
      <p className="font-body text-text mb-8 max-w-xl text-lg">{t('member.start.body')}</p>

      <div className="grid gap-4 md:grid-cols-3">
        {(['day_ticket', 'flex_desk', 'private_desk'] as const).map((key) => {
          const price = priceOf(key);
          return (
            <Card key={key}>
              <h2 className="font-heading text-ink mb-1 text-xl">{t(`product.${key}.name`)}</h2>
              {price ? (
                <PriceTag
                  grossCents={price.grossCents}
                  netCents={price.netCents}
                  period={price.period as 'day' | 'month'}
                />
              ) : null}
              <Link
                to={key === 'day_ticket' ? '/platz' : `/platz?product=${key}`}
                className="text-teal-dark font-body mt-4 inline-block underline"
              >
                {t('member.start.choose')}
              </Link>
            </Card>
          );
        })}
      </div>
    </main>
  );
}
